import React, { useState } from 'react';
import { List, FunctionField, TextInput, useNotify, useRefresh, useTranslate } from 'react-admin';
import { Box, Chip, Typography } from '@mui/material';
import {
  EditRounded as EditIcon, DeleteOutlineRounded as DeleteIcon,
  LockOutlined as LockIcon, LockOpenOutlined as UnlockIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { httpClient } from '@/utils/httpClient';
import {
  ConfirmDeleteDialog, HelpDialog, ListScreen, PageTitle, ResponsiveDatagrid, RowActionsMenu, SectionHeader,
} from '@/components/shell';

interface CostCenterRecord {
  id: number | string;
  name: string;
  description?: string;
  unpaidCreditLimit?: number | null;
  isCreditsBlocked: boolean;
  creditBalance?: { availableCredits?: number; reservedCredits?: number } | null;
}

const CostCenterName: React.FC<{ record: CostCenterRecord }> = ({ record }) => (
  <Box component="span" sx={{ display: 'block' }}>
    <Typography variant="body2" component="span" sx={{ display: 'block' }}>{record.name}</Typography>
    {record.description && (
      <Typography variant="body2" color="text.secondary" component="span" sx={{ display: 'block' }}>
        {record.description}
      </Typography>
    )}
  </Box>
);

const UnpaidLimit: React.FC<{ record: CostCenterRecord }> = ({ record }) => {
  const t = useTranslate();
  if (record.unpaidCreditLimit != null && record.unpaidCreditLimit > 0) {
    return <>{t('screens.costCenters.credits', { count: record.unpaidCreditLimit.toLocaleString() })}</>;
  }
  return (
    <Typography variant="body2" color="text.secondary" component="span">
      {t('screens.costCenters.noLimit')}
    </Typography>
  );
};

const AvailableCredits: React.FC<{ record: CostCenterRecord }> = ({ record }) => {
  const t = useTranslate();
  const balance = record.creditBalance;
  if (!balance) return <Typography variant="body2" color="text.secondary" component="span">—</Typography>;
  return (
    <Box component="span" sx={{ display: 'block', fontVariantNumeric: 'tabular-nums' }}>
      <Typography variant="body2" component="span" sx={{ display: 'block' }}>
        {balance.availableCredits?.toLocaleString() ?? 0}
      </Typography>
      {(balance.reservedCredits ?? 0) > 0 && (
        <Typography variant="body2" color="text.secondary" component="span" sx={{ display: 'block' }}>
          {t('screens.costCenters.reserved', { count: balance.reservedCredits })}
        </Typography>
      )}
    </Box>
  );
};

/** Active in words; a blocked center is the exception, so only that one gets a chip. */
const CreditStatus: React.FC<{ record: CostCenterRecord }> = ({ record }) => {
  const t = useTranslate();
  if (record.isCreditsBlocked) return <Chip label={t('screens.costCenters.blocked')} color="error" size="small" />;
  return <>{t('screens.costCenters.active')}</>;
};

const CostCenterActions: React.FC<{ record: CostCenterRecord }> = ({ record }) => {
  const t = useTranslate();
  const navigate = useNavigate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [deleteOpen, setDeleteOpen] = useState(false);

  const toggleBlock = async () => {
    try {
      const endpoint = record.isCreditsBlocked
        ? `/admin/cost-center/UnblockCredits/${record.id}`
        : `/admin/cost-center/BlockCredits/${record.id}`;
      await httpClient.post(endpoint, {});
      notify(record.isCreditsBlocked ? 'screens.costCenters.unblockedDone' : 'screens.costCenters.blockedDone');
      refresh();
    } catch {
      notify('screens.costCenters.blockFailed', { type: 'error' });
    }
  };

  return (
    <>
      <RowActionsMenu
        actions={[
          {
            key: 'edit',
            label: t('ra.action.edit'),
            icon: <EditIcon fontSize="small" />,
            onClick: () => navigate(`/cost-centers/${record.id}`),
          },
          {
            key: 'block',
            label: t(record.isCreditsBlocked ? 'screens.costCenters.unblock' : 'screens.costCenters.block'),
            icon: record.isCreditsBlocked ? <UnlockIcon fontSize="small" /> : <LockIcon fontSize="small" />,
            onClick: toggleBlock,
          },
          {
            key: 'delete',
            label: t('ra.action.delete'),
            icon: <DeleteIcon fontSize="small" />,
            color: 'error',
            dividerBefore: true,
            onClick: () => setDeleteOpen(true),
          },
        ]}
      />
      <ConfirmDeleteDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        name={record.name}
        onDelete={() => httpClient.delete(`/admin/cost-center/DeleteCostCenter/${record.id}`)}
      />
    </>
  );
};

const HELP_TOPICS = ['Limit', 'Status', 'Accounts'] as const;

const CostCenterHelp: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const t = useTranslate();
  return (
    <HelpDialog open={open} onClose={onClose} title={t('screens.costCenters.helpTitle')}>
      <Typography variant="body2">{t('screens.costCenters.helpIntro')}</Typography>
      {HELP_TOPICS.map((topic) => (
        <Box key={topic}>
          <Typography variant="subtitle2">{t(`screens.costCenters.help${topic}Title`)}</Typography>
          <Typography variant="body2" color="text.secondary">{t(`screens.costCenters.help${topic}`)}</Typography>
        </Box>
      ))}
    </HelpDialog>
  );
};

export const CostCenterList = () => {
  const t = useTranslate();
  const navigate = useNavigate();
  const [helpOpen, setHelpOpen] = useState(false);

  const filters = [<TextInput key="q" source="q" label={t('ra.action.search')} alwaysOn resettable />];

  return (
    <ListScreen
      header={
        <>
          <SectionHeader
            left={
              <PageTitle
                title={t('resources.cost-centers.name', { smart_count: 2 })}
                subtitle={t('screens.costCenters.subtitle')}
              />
            }
            help={{ label: t('screens.costCenters.help'), onClick: () => setHelpOpen(true) }}
            create={{ label: t('common.create'), onClick: () => navigate('/cost-centers/create') }}
          />
          <CostCenterHelp open={helpOpen} onClose={() => setHelpOpen(false)} />
        </>
      }
    >
      <List filters={filters} actions={false} empty={false} perPage={10}>
        <ResponsiveDatagrid<CostCenterRecord>
          rowClick="edit"
          mobile={{
            primary: (r) => r.name,
            status: (r) => <CreditStatus record={r} />,
            lines: [
              { label: t('screens.costCenters.available'), render: (r) => <AvailableCredits record={r} /> },
              { label: t('screens.costCenters.unpaidLimit'), render: (r) => <UnpaidLimit record={r} /> },
            ],
            actions: (r) => <CostCenterActions record={r} />,
            onClick: (r) => navigate(`/cost-centers/${r.id}`),
          }}
        >
          <FunctionField
            label={t('screens.costCenters.name')}
            sortable={false}
            render={(r: CostCenterRecord) => <CostCenterName record={r} />}
          />
          <FunctionField
            label={t('screens.costCenters.unpaidLimit')}
            sortable={false}
            textAlign="right"
            render={(r: CostCenterRecord) => <UnpaidLimit record={r} />}
          />
          <FunctionField
            label={t('screens.costCenters.available')}
            sortable={false}
            textAlign="right"
            render={(r: CostCenterRecord) => <AvailableCredits record={r} />}
          />
          <FunctionField
            label={t('screens.costCenters.status')}
            sortable={false}
            render={(r: CostCenterRecord) => <CreditStatus record={r} />}
          />
          <FunctionField
            label=""
            textAlign="right"
            sortable={false}
            render={(r: CostCenterRecord) => <CostCenterActions record={r} />}
          />
        </ResponsiveDatagrid>
      </List>
    </ListScreen>
  );
};
